import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { useWindowWidth } from '../hooks/useWindowWidth';

const INK  = '#0A1F4A';
const INK2 = '#3D4F78';
const INK3 = '#7883A2';
const BG   = '#ECEEF3';
const SURF = '#FFFFFF';
const RULE = '#D6DAE4';
const ACC  = '#FF7A3D';
const HOT  = '#FFD500';

const container = { maxWidth: 1200, margin: '0 auto', padding: '0 24px' };

function Kicker({ children, light }) {
  return (
    <span style={{
      display: 'inline-block',
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: 11, fontWeight: 600,
      letterSpacing: '0.14em', textTransform: 'uppercase',
      padding: '4px 10px',
      background: light ? 'rgba(255,255,255,0.12)' : INK,
      color: light ? ACC : ACC,
      borderRadius: 4,
      marginBottom: 16,
    }}>
      {children}
    </span>
  );
}

/* ─── Hero ───────────────────────────────────────────────────── */
function Hero({ isMobile }) {
  return (
    <section style={{
      padding: isMobile ? '72px 0 80px' : '100px 0 120px',
      background: INK,
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Background glow blobs */}
      <div style={{ position: 'absolute', left: -100, top: -100, width: 400, height: 400, borderRadius: '50%', background: ACC, opacity: 0.08, filter: 'blur(60px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', right: -60, bottom: -80, width: 300, height: 300, borderRadius: '50%', background: HOT, opacity: 0.1, filter: 'blur(50px)', pointerEvents: 'none' }} />

      <div style={{ ...container, position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 820, margin: '0 auto', padding: '0 24px' }}>
        <Kicker light>The workflow</Kicker>
        <h1 style={{
          fontSize: isMobile ? 40 : 72,
          lineHeight: 0.97,
          letterSpacing: '-0.04em',
          fontWeight: 700,
          color: BG,
          marginBottom: 24,
        }}>
          Yesterday's tenders,{' '}
          <span style={{
            background: ACC,
            color: '#fff',
            padding: '2px 10px',
            display: 'inline-block',
            transform: 'rotate(-1deg)',
            boxShadow: `4px 4px 0 ${HOT}`,
          }}>
            scored and waiting
          </span>
          {' '}each morning.
        </h1>
        <p style={{ fontSize: isMobile ? 17 : 20, color: 'rgba(236,238,243,0.7)', maxWidth: 620, margin: '0 auto 40px', lineHeight: 1.5 }}>
          Jyto sits between public procurement portals and your sales team — automatically filtering, scoring, and routing every relevant opportunity so you start each day with a ranked shortlist, not a search task.
        </p>

        {/* Flow pill */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0,
          background: 'rgba(255,255,255,0.07)',
          border: '1px solid rgba(255,255,255,0.14)',
          borderRadius: 999,
          padding: '6px 8px',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          justifyContent: 'center',
        }}>
          {[
            { label: 'Tender published', color: INK2 },
            null,
            { label: 'Jyto', color: ACC, bold: true },
            null,
            { label: 'Your sales pipeline', color: INK2 },
          ].map((item, i) => item === null ? (
            <span key={i} style={{ color: 'rgba(255,255,255,0.3)', fontSize: 16, padding: '0 4px' }}>→</span>
          ) : (
            <span key={i} style={{
              fontSize: 13, fontWeight: item.bold ? 700 : 500,
              color: item.bold ? ACC : 'rgba(236,238,243,0.7)',
              padding: '6px 14px',
              background: item.bold ? 'rgba(255,122,61,0.15)' : 'transparent',
              borderRadius: 999,
            }}>
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Flow diagram ───────────────────────────────────────────── */
function FlowDiagram({ isMobile }) {
  const stages = [
    {
      num: '01',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
        </svg>
      ),
      iconBg: INK,
      iconColor: ACC,
      title: 'Tender gets published',
      subtitle: 'From procurement authority to portal',
      desc: 'A contracting authority — a municipality, ministry, or public agency — publishes a new call for tender on TED, a national e-procurement portal, or a regional journal. This happens thousands of times a week across the EU.',
      tags: ['TED / OJEU', 'National portals', 'Regional journals', '14,000+ tenders/week'],
    },
    {
      num: '02',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07M8.46 8.46a5 5 0 0 0 0 7.07"/>
        </svg>
      ),
      iconBg: ACC,
      iconColor: '#fff',
      title: 'Jyto goes to work',
      subtitle: 'Crawl · score · filter · route',
      desc: 'Each night, Jyto picks up the previous day\'s tenders, parses the full notice, and scores each one 0–100 against every active company profile. Anything below your threshold is filtered out. Relevant matches are queued for your morning digest.',
      tags: ['Daily ingestion', 'AI-scored 0–100', 'Profile-matched', 'Duplicate-collapsed'],
    },
    {
      num: '03',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
      ),
      iconBg: HOT,
      iconColor: INK,
      title: 'Arrives in your pipeline',
      subtitle: 'Ready to act on, not research',
      desc: 'The scored opportunity lands where your team works — morning email digest, a Slack message, a new deal in HubSpot or Salesforce, or directly in the Jyto dashboard. Your bid manager opens it with context already there: score, deadline, value, and the one-line reason it matched.',
      tags: ['Email digest', 'Slack', 'HubSpot / Salesforce', 'Dashboard'],
    },
  ];

  return (
    <section style={{ padding: '100px 0', background: BG }}>
      <div style={container}>
        <div style={{ maxWidth: 720, margin: '0 auto 64px', textAlign: 'center' }}>
          <Kicker>Three stages</Kicker>
          <h2 style={{ fontSize: isMobile ? 32 : 52, lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 700, color: INK, marginBottom: 16 }}>
            The full journey, explained.
          </h2>
          <p style={{ fontSize: 18, color: INK2 }}>
            No manual searching. No missed notices. No noise in your inbox.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: 20,
          position: 'relative',
        }}>
          {/* Connector line (desktop only) */}
          {!isMobile && (
            <div style={{
              position: 'absolute',
              top: 52,
              left: 'calc(33.33% - 0px)',
              right: 'calc(33.33% - 0px)',
              height: 2,
              background: `linear-gradient(90deg, ${RULE} 0%, ${ACC} 50%, ${RULE} 100%)`,
              zIndex: 0,
              pointerEvents: 'none',
            }} />
          )}

          {stages.map((stage, i) => (
            <div
              key={stage.num}
              style={{
                background: SURF,
                border: `1px solid ${INK}`,
                borderRadius: 20,
                padding: '32px 28px',
                display: 'flex', flexDirection: 'column', gap: 16,
                position: 'relative', zIndex: 1,
                transition: 'transform 220ms, box-shadow 220ms',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = `0 10px 0 ${INK}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{
                position: 'absolute', top: 20, right: 24,
                fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: INK3,
              }}>{stage.num}</span>

              <div style={{
                width: 48, height: 48,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 12,
                background: stage.iconBg,
                color: stage.iconColor,
                flexShrink: 0,
              }}>
                {stage.icon}
              </div>

              <div>
                <h3 style={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.01em', color: INK, marginBottom: 4 }}>
                  {stage.title}
                </h3>
                <div style={{
                  fontSize: 12, fontFamily: '"JetBrains Mono", monospace',
                  color: INK3, letterSpacing: '0.02em',
                }}>
                  {stage.subtitle}
                </div>
              </div>

              <p style={{ fontSize: 15, color: INK2, lineHeight: 1.6, margin: 0 }}>
                {stage.desc}
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {stage.tags.map((tag) => (
                  <span key={tag} style={{
                    fontSize: 11, fontWeight: 600,
                    fontFamily: '"JetBrains Mono", monospace',
                    color: INK2,
                    background: BG,
                    border: `1px solid ${RULE}`,
                    padding: '3px 8px', borderRadius: 4,
                  }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Stage deep-dives ───────────────────────────────────────── */
function StageDeepDive({ isMobile }) {
  return (
    <section style={{ padding: '20px 0 100px', background: BG }}>
      <div style={container}>

        {/* Stage 1 */}
        <div style={{
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 64,
          alignItems: 'center',
          padding: '80px 0',
          borderTop: `1px solid ${RULE}`,
        }}>
          <div style={isMobile ? { marginBottom: 40 } : {}}>
            <div style={{
              display: 'inline-block',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 11, fontWeight: 600,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              padding: '4px 10px',
              background: INK, color: ACC, borderRadius: 4,
              marginBottom: 20,
            }}>Stage 01 — Publication</div>
            <h2 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 700, letterSpacing: '-0.025em', color: INK, marginBottom: 16, lineHeight: 1.1 }}>
              Every EU tender from the previous day, processed overnight.
            </h2>
            <p style={{ fontSize: 16, color: INK2, lineHeight: 1.65, marginBottom: 20 }}>
              Public contracts in Europe must be advertised openly. That means thousands of new notices appear every week across dozens of portals — from the EU-wide TED journal to country-specific platforms like Tenderned, DOFFIN, or Vergabe.NRW.
            </p>
            <p style={{ fontSize: 16, color: INK2, lineHeight: 1.65 }}>
              Most companies miss the majority of these. Manual searches are slow, incomplete, and biased toward the portals your team already knows about. Jyto covers them all.
            </p>
          </div>
          <div style={{
            background: SURF,
            border: `1px solid ${INK}`,
            borderRadius: 16,
            padding: 28,
            boxShadow: `8px 8px 0 ${INK}`,
          }}>
            <div style={{
              fontFamily: '"JetBrains Mono", monospace', fontSize: 11,
              color: INK3, marginBottom: 20, letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              Portals monitored
            </div>
            {[
              { name: 'TED / OJEU', region: 'EU-wide', count: '8,400+ notices/week', dot: ACC },
              { name: 'Tenderned', region: 'Netherlands', count: '1,200+ notices/week', dot: HOT },
              { name: 'DOFFIN', region: 'Norway', count: '340+ notices/week', dot: INK },
              { name: 'Find a Tender', region: 'United Kingdom', count: '580+ notices/week', dot: INK2 },
              { name: '+ 40 more', region: 'DE, FR, BE, DK, SE…', count: 'Expanding monthly', dot: RULE },
            ].map((p) => (
              <div key={p.name} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 0',
                borderBottom: `1px solid ${RULE}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.dot, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: INK }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: INK3, fontFamily: '"JetBrains Mono", monospace' }}>{p.region}</div>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: INK2, fontFamily: '"JetBrains Mono", monospace', textAlign: 'right' }}>{p.count}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Stage 2 */}
        <div style={{
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 64,
          alignItems: 'center',
          padding: '80px 0',
          borderTop: `1px solid ${RULE}`,
        }}>
          {/* Card first on desktop for visual variety */}
          {!isMobile && (
            <div style={{
              background: INK,
              border: `1px solid rgba(255,255,255,0.1)`,
              borderRadius: 16,
              padding: 28,
              boxShadow: `8px 8px 0 ${ACC}`,
            }}>
              <div style={{
                fontFamily: '"JetBrains Mono", monospace', fontSize: 11,
                color: 'rgba(236,238,243,0.5)', marginBottom: 20, letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>
                Scoring pipeline
              </div>
              {[
                { step: 'Parse', label: 'Extract title, CPV codes, value, deadline, authority', color: ACC },
                { step: 'Embed', label: 'Encode full notice into semantic vector space', color: HOT },
                { step: 'Match', label: 'Compare against your company profile embedding', color: ACC },
                { step: 'Score', label: 'Output 0–100 + one-line match reason', color: HOT },
                { step: 'Filter', label: 'Drop anything below your threshold (default: 60)', color: 'rgba(236,238,243,0.4)' },
                { step: 'Route', label: 'Queue for delivery via your configured channel', color: 'rgba(236,238,243,0.4)' },
              ].map((row, i) => (
                <div key={row.step} style={{
                  display: 'grid', gridTemplateColumns: '64px 1fr',
                  gap: 12, alignItems: 'flex-start',
                  padding: '10px 0',
                  borderBottom: i < 5 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                }}>
                  <div style={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 11, fontWeight: 700,
                    color: row.color,
                    letterSpacing: '0.06em',
                    paddingTop: 2,
                  }}>
                    {row.step}
                  </div>
                  <div style={{ fontSize: 13, color: 'rgba(236,238,243,0.65)', lineHeight: 1.5 }}>{row.label}</div>
                </div>
              ))}
            </div>
          )}
          <div style={isMobile ? { marginBottom: 40 } : {}}>
            <div style={{
              display: 'inline-block',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 11, fontWeight: 600,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              padding: '4px 10px',
              background: INK, color: ACC, borderRadius: 4,
              marginBottom: 20,
            }}>Stage 02 — Processing</div>
            <h2 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 700, letterSpacing: '-0.025em', color: INK, marginBottom: 16, lineHeight: 1.1 }}>
              Scored, filtered, and matched — before your morning coffee.
            </h2>
            <p style={{ fontSize: 16, color: INK2, lineHeight: 1.65, marginBottom: 20 }}>
              Each night, Jyto's pipeline picks up the previous day's notices. We parse the full text, extract structured data, and score each one against every active company profile using semantic matching — not just keyword overlap.
            </p>
            <p style={{ fontSize: 16, color: INK2, lineHeight: 1.65 }}>
              Each tender gets a 0–100 relevance score and a one-line explanation. You set the threshold. Anything below it never reaches your team.
            </p>
          </div>
          {isMobile && (
            <div style={{
              background: INK, border: `1px solid rgba(255,255,255,0.1)`, borderRadius: 16, padding: 28, boxShadow: `8px 8px 0 ${ACC}`,
            }}>
              {[
                { step: 'Parse', color: ACC }, { step: 'Embed', color: HOT },
                { step: 'Match', color: ACC }, { step: 'Score', color: HOT },
                { step: 'Filter', color: 'rgba(236,238,243,0.4)' }, { step: 'Route', color: 'rgba(236,238,243,0.4)' },
              ].map((row, i) => (
                <div key={row.step} style={{ padding: '8px 0', borderBottom: i < 5 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                  <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, fontWeight: 700, color: row.color }}>{row.step}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stage 3 */}
        <div style={{
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 64,
          alignItems: 'center',
          padding: '80px 0',
          borderTop: `1px solid ${RULE}`,
        }}>
          <div style={isMobile ? { marginBottom: 40 } : {}}>
            <div style={{
              display: 'inline-block',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 11, fontWeight: 600,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              padding: '4px 10px',
              background: INK, color: ACC, borderRadius: 4,
              marginBottom: 20,
            }}>Stage 03 — Delivery</div>
            <h2 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 700, letterSpacing: '-0.025em', color: INK, marginBottom: 16, lineHeight: 1.1 }}>
              It shows up where your team already works.
            </h2>
            <p style={{ fontSize: 16, color: INK2, lineHeight: 1.65, marginBottom: 20 }}>
              A matched opportunity arrives as a deal in HubSpot or Salesforce, a Slack message in your #bids channel, or a line in your morning digest — with the score, deadline, contract value, and reason it matched already attached.
            </p>
            <p style={{ fontSize: 16, color: INK2, lineHeight: 1.65 }}>
              Your bid manager's decision is simple: go or no-go. No search, no copy-paste, no spreadsheet. The context is there. The deadline is clear. The decision is theirs.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              {
                channel: 'Email digest',
                time: '07:00 daily',
                desc: 'Ranked list of yesterday\'s matches, straight to your inbox.',
                icon: '✉',
                accent: INK,
              },
              {
                channel: 'Slack',
                time: 'Each morning',
                desc: 'New high-score matches → summary message in #tenders.',
                icon: '#',
                accent: HOT,
              },
              {
                channel: 'HubSpot / Salesforce',
                time: 'Auto-created',
                desc: 'New deal created with tender details pre-filled as a CRM record.',
                icon: '⬡',
                accent: ACC,
              },
              {
                channel: 'Dashboard',
                time: 'Always live',
                desc: 'Browse, filter, and save matches across all your profiles.',
                icon: '▦',
                accent: INK2,
              },
            ].map((ch) => (
              <div key={ch.channel} style={{
                display: 'flex', alignItems: 'center', gap: 16,
                background: SURF,
                border: `1px solid ${RULE}`,
                borderRadius: 12,
                padding: '16px 20px',
                transition: 'border-color 160ms',
              }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = INK}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = RULE}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: ch.accent, color: ch.accent === INK || ch.accent === INK2 ? ACC : INK,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, fontWeight: 700, flexShrink: 0,
                }}>
                  {ch.icon}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: INK }}>{ch.channel}</div>
                  <div style={{ fontSize: 12, color: INK2, marginTop: 2 }}>{ch.desc}</div>
                </div>
                <div style={{
                  marginLeft: 'auto', flexShrink: 0,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 10, color: INK3,
                  background: BG, border: `1px solid ${RULE}`,
                  padding: '3px 8px', borderRadius: 4,
                }}>
                  {ch.time}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─── What your team sees ────────────────────────────────────── */
function WhatYouSee({ isMobile }) {
  return (
    <section style={{ padding: '100px 0', background: INK, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', right: -80, top: -60, width: 360, height: 360, borderRadius: '50%', background: ACC, opacity: 0.1, filter: 'blur(50px)', pointerEvents: 'none' }} />

      <div style={{ ...container, position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 720, margin: '0 auto 56px', textAlign: 'center' }}>
          <Kicker light>What you actually get</Kicker>
          <h2 style={{ fontSize: isMobile ? 32 : 52, lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 700, color: BG, marginBottom: 16 }}>
            Ready to decide. Not to research.
          </h2>
          <p style={{ fontSize: 18, color: 'rgba(236,238,243,0.65)' }}>
            Every opportunity arrives with the information your team needs to make a go/no-go call in under a minute.
          </p>
        </div>

        {/* Mock match card */}
        <div style={{
          maxWidth: 760, margin: '0 auto',
          background: SURF,
          border: `1px solid rgba(255,255,255,0.15)`,
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: `0 0 0 1px rgba(255,255,255,0.06), 0 24px 48px rgba(0,0,0,0.4)`,
        }}>
          {/* Window bar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 16px', borderBottom: `1px solid ${RULE}`,
            background: BG,
          }}>
            <div style={{ display: 'flex', gap: 5 }}>
              {[ACC, HOT, RULE].map((c, i) => (
                <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: INK3, fontFamily: '"JetBrains Mono", monospace' }}>
              ~/matches · Profile: IT Services NL · 3 new
            </div>
            <div />
          </div>

          {/* Match rows */}
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              {
                score: 94, band: 'hi',
                title: 'Cloud infrastructure migration — Port of Rotterdam Authority',
                meta: 'NL · CPV 72222300 · Deadline 18 Jun · Posted 2h ago',
                value: '€ 2.4 M',
                sub: 'contract value',
                reason: 'Strong match on cloud migration + Dutch public sector experience',
              },
              {
                score: 88, band: 'md',
                title: 'Cybersecurity audit & penetration testing — e-Health Estonia',
                meta: 'EE · CPV 72811000 · Deadline 9 Jun · Posted 4h ago',
                value: '€ 430 k',
                sub: '12-month contract',
                reason: 'Security audit scope aligns with your ISO 27001 credential',
              },
              {
                score: 81, band: 'md',
                title: 'Digital transformation advisory — Province of Noord-Holland',
                meta: 'NL · CPV 72600000 · Deadline 27 Jun · Posted 7h ago',
                value: '€ 890 k',
                sub: '18-month framework',
                reason: 'Digital transformation + public sector advisory matches profile',
              },
            ].map((r) => (
              <div key={r.score + r.title} style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '40px 1fr' : '50px 1fr auto',
                gap: 14, alignItems: 'center',
                padding: '14px 16px',
                border: `1px solid ${RULE}`,
                borderRadius: 12,
                background: SURF,
              }}>
                {/* Score badge */}
                <div style={{
                  width: isMobile ? 40 : 50, height: isMobile ? 40 : 50,
                  display: 'grid', placeItems: 'center',
                  fontWeight: 700, fontSize: isMobile ? 14 : 16, borderRadius: 10,
                  fontFamily: '"JetBrains Mono", monospace',
                  background: r.band === 'hi' ? ACC : HOT,
                  color: r.band === 'hi' ? '#fff' : INK,
                  flexShrink: 0,
                }}>
                  {r.score}
                </div>
                {/* Content */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: INK, lineHeight: 1.3, marginBottom: 4 }}>{r.title}</div>
                  <div style={{ fontSize: 11, color: INK3, fontFamily: '"JetBrains Mono", monospace', marginBottom: 6 }}>{r.meta}</div>
                  <div style={{
                    fontSize: 12, color: INK2,
                    background: BG, borderRadius: 6,
                    padding: '4px 8px', display: 'inline-block',
                    fontStyle: 'italic',
                  }}>
                    "{r.reason}"
                  </div>
                </div>
                {/* Value */}
                {!isMobile && (
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>{r.value}</div>
                    <div style={{ fontSize: 10, color: INK3, fontFamily: '"JetBrains Mono", monospace', marginTop: 2 }}>{r.sub}</div>
                  </div>
                )}
              </div>
            ))}

            <div style={{
              textAlign: 'center', padding: '10px 0 4px',
              fontSize: 12, color: INK3, fontFamily: '"JetBrains Mono", monospace',
            }}>
              + 4 more matches below threshold · <span style={{ color: INK2 }}>Lower threshold to see</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── CTA band ───────────────────────────────────────────────── */
function CTABand({ isMobile }) {
  return (
    <section style={{ padding: '72px 24px' }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        background: ACC,
        border: `1px solid ${INK}`,
        borderRadius: 24,
        padding: isMobile ? '48px 24px' : '64px 56px',
        position: 'relative', overflow: 'hidden',
        display: isMobile ? 'block' : 'grid',
        gridTemplateColumns: '1.4fr 1fr',
        gap: 32, alignItems: 'center',
      }}>
        <div style={{ position: 'absolute', right: -60, top: -60, width: 260, height: 260, borderRadius: '50%', background: HOT, opacity: 0.35, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontSize: isMobile ? 30 : 48, fontWeight: 700, letterSpacing: '-0.03em', color: '#fff', marginBottom: 10 }}>
            See it in your pipeline<br />within 14 days.
          </h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.8)', margin: 0 }}>
            Set up a profile in 5 minutes. Free trial, no credit card.
          </p>
        </div>
        <div style={{
          display: 'flex', gap: 12,
          justifyContent: isMobile ? 'flex-start' : 'flex-end',
          marginTop: isMobile ? 28 : 0,
          flexWrap: 'wrap', position: 'relative', zIndex: 1,
        }}>
          <Link to="/signup" style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '13px 22px', borderRadius: 10,
            fontSize: 15, fontWeight: 600,
            background: INK, color: BG,
            textDecoration: 'none',
            boxShadow: `0 4px 0 0 rgba(0,0,0,0.25)`,
          }}>
            Start free trial →
          </Link>
          <Link to="/pricing" style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '13px 22px', borderRadius: 10,
            fontSize: 15, fontWeight: 500,
            background: 'transparent', color: '#fff',
            border: '1px solid rgba(255,255,255,0.6)',
            textDecoration: 'none',
          }}>
            See pricing
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ───────────────────────────────────────────────────── */
export default function HowItWorks() {
  const width = useWindowWidth();
  const isMobile = width < 768;

  return (
    <div style={{ background: BG }}>
      <Hero isMobile={isMobile} />
      <FlowDiagram isMobile={isMobile} />
      <StageDeepDive isMobile={isMobile} />
      <WhatYouSee isMobile={isMobile} />
      <CTABand isMobile={isMobile} />
      <Footer />
    </div>
  );
}
